import React from 'react';
import { Popover } from 'antd';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { useStore } from 'zustand';

import { CalendarDTO, calendarStore, EventId } from 'entities/calendar';
import { isWeekday } from '../../lib/isWeekday';

import { CalendarPopoverContent } from '../CalendarPopoverContent';
import styles from './DayCell.module.scss';

interface DayCellProps {
  date: Dayjs;
  dayEvents: CalendarDTO['events'];
  isBlocked: boolean;
  isSelected: boolean;
}

const DayCell = React.forwardRef<HTMLDivElement, DayCellProps>(({ date, dayEvents, isBlocked, isSelected }, ref) => {
  const formattedDate = date.format('YYYY-MM-DD');

  const openedEventId = useStore(calendarStore, (state) => state.openedEventId);
  const setOpenedEventId = useStore(calendarStore, (state) => state.setOpenedEventId);

  const handleEventOpenChange = (open: boolean, eventDates: EventId): void => {
    setOpenedEventId(open ? eventDates : null);
  };

  return (
    <div
      className={classNames(styles.dayCell, {
        isWeekend: isWeekday(date),
        isBlocked,
        isSelected,
      })}
    >
      <div className={styles.events}>
        {dayEvents.map((event) => {
          const isFirstDay = dayjs(event.startDate).format('YYYY-MM-DD') === formattedDate;
          const isLastDay = dayjs(event.endDate).format('YYYY-MM-DD') === formattedDate;
          const isMiddleDay = !isFirstDay && !isLastDay;
          const isSingleDay = isFirstDay && isLastDay;

          const eventDates = {
            startDate: event.startDate,
            endDate: event.endDate,
            clickedDate: formattedDate,
          };

          const isEventSelected =
            openedEventId?.startDate === event.startDate && openedEventId?.endDate === event.endDate;
          const showPopup = isEventSelected && openedEventId?.clickedDate === formattedDate;

          return (
            <Popover
              open={showPopup}
              onOpenChange={(open) => {
                if (isBlocked) return;
                handleEventOpenChange(open, eventDates);
              }}
              content={<CalendarPopoverContent event={event} />}
              trigger="click"
              key={event.id}
            >
              <div
                className={classNames(styles.event, styles[event.exclusionType.toLowerCase()], {
                  [styles.eventSingleDay]: isSingleDay && isEventSelected,
                  [styles.eventStart]: !isSingleDay && isFirstDay && isEventSelected,
                  [styles.eventMiddle]: !isSingleDay && isMiddleDay && isEventSelected,
                  [styles.eventEnd]: !isSingleDay && isLastDay && isEventSelected,
                })}
                ref={ref}
              >
                {(isFirstDay || isSingleDay) && event.name}
              </div>
            </Popover>
          );
        })}
      </div>
    </div>
  );
});

export default React.memo(DayCell);
